import {
    CreateOrUpdateDebriefing,
    DebriefingClientRollup,
    DebriefingDetails,
    DebriefingListElement,
    DebriefingRelationshipOwners,
} from 'pages/debriefing/types';
import { objectToQueryParams } from 'utils/linkUtil';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

export const APIDebriefingsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: ['DebriefingListItems', 'DebriefingsCount', 'Debriefing', 'DebriefingClientRollup'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getDebriefingsWithFilters: accumulatedQueryWithFilters<DebriefingListElement>(builder, '/v1/debriefings', {
                secondarySort: ['debriefingDate,desc'],
                providesTags: ['DebriefingListItems'],
            }),
            // Restricted rows are counted too — the BE count deliberately applies no access filter.
            getDebriefingsCount: injectCountEndPoint(builder, '/v1/debriefings', 'DebriefingsCount'),
            getDebriefingById: builder.query<DebriefingDetails, string>({
                query: (id) => `/v1/debriefings/${id}`,
                keepUnusedDataFor: 0,
                providesTags: (_result, _error, id) => [{ type: 'Debriefing', id }],
            }),
            addDebriefing: builder.mutation<DebriefingDetails, CreateOrUpdateDebriefing>({
                query: (body) => ({
                    url: `/v1/debriefings`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['DebriefingListItems', 'DebriefingsCount', 'DebriefingClientRollup'],
            }),
            updateDebriefing: builder.mutation<DebriefingDetails, CreateOrUpdateDebriefing & { id: number }>({
                query: ({ id, ...body }) => ({
                    url: `/v1/debriefings/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [
                    { type: 'Debriefing', id: String(id) },
                    'DebriefingListItems',
                    'DebriefingsCount',
                    'DebriefingClientRollup',
                ],
            }),
            deleteDebriefing: builder.mutation<void, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/debriefings/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['DebriefingListItems', 'DebriefingsCount', 'DebriefingClientRollup'],
            }),
            // Deliberately not viewer-access-filtered on the BE: aggregates the client's full
            // debriefing set, including rows the viewer cannot open.
            getDebriefingClientRollup: builder.query<DebriefingClientRollup, { clientPartyId: number }>({
                query: ({ clientPartyId }) => `/v1/debriefings/client-rollup${objectToQueryParams({ clientPartyId })}`,
                providesTags: (_result, _error, { clientPartyId }) => [
                    { type: 'DebriefingClientRollup', id: clientPartyId },
                ],
            }),
            getDebriefingRelationshipOwners: builder.query<DebriefingRelationshipOwners, { clientPartyId: number }>({
                query: ({ clientPartyId }) =>
                    `/v1/debriefings/relationship-owners${objectToQueryParams({ clientPartyId })}`,
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetDebriefingsWithFiltersQuery,
    useLazyGetDebriefingsWithFiltersQuery,
    useLazyGetDebriefingsCountQuery,
    useGetDebriefingByIdQuery,
    useLazyGetDebriefingByIdQuery,
    useAddDebriefingMutation,
    useUpdateDebriefingMutation,
    useDeleteDebriefingMutation,
    useGetDebriefingClientRollupQuery,
    useGetDebriefingRelationshipOwnersQuery,
    useLazyGetDebriefingRelationshipOwnersQuery,
} = APIDebriefingsSlice;
